<template>
    <v-container fluid class="py-4">
        <v-row justify="center">
            <v-col cols="12" md="10">
                <v-card class="pa-4 mb-4" elevation="1">
                    <v-card-title class="subtitle-1 font-weight-bold">Envío masivo de correos</v-card-title>
                    <v-divider class="mb-4" />
                    <v-row>
                        <v-col cols="12">
                            <v-text-field
                                v-model="form.asunto"
                                label="Asunto del correo"
                                prepend-icon="mdi-email-outline"
                                dense
                                outlined
                                color="primary"
                                required
                                :rules="[(v) => !!v || 'El asunto es requerido']"
                            />
                        </v-col>

                        <v-col cols="12" md="6">
                            <v-select
                                v-model="form.remitente"
                                :items="resources.remitentes"
                                label="Remitente"
                                prepend-icon="mdi-account-circle"
                                dense
                                outlined
                                color="primary"
                                required
                                :rules="[(v) => !!v || 'El remitente es requerido']"
                                item-text="nombre"
                                item-value="email"
                                placeholder="Selecciona un remitente"
                            />
                        </v-col>

                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model="form.bcc"
                                label="BCC (Copia oculta)"
                                prepend-icon="mdi-account-multiple"
                                dense
                                outlined
                                color="primary"
                                type="email"
                                :rules="[(v) => !v || validateEmail(v) || 'Correo inválido']"
                                placeholder="ejemplo@mail.com"
                            />
                        </v-col>

                        <v-col cols="12" md="12" class="d-flex flex-column">
                            <div class="mb-2 font-weight-medium" style="font-size: 1.1rem; color: #444">
                                Contenido del correo
                            </div>
                            <v-alert type="info" color="primary" border="left" elevation="0" dense class="mb-3">
                                <strong>Nota importante:</strong>
                                Para asegurar que tu correo se visualice correctamente en todos los clientes (incluido
                                Gmail),
                                <span style="font-weight: 600">
                                    maqueta el contenido usando
                                    <u>tablas HTML</u>
                                    y estilos
                                    <u>CSS inline</u>
                                    básicos
                                </span>
                                . Evita
                                <b>CSS Grid</b>
                                ,
                                <b>Flexbox</b>
                                y propiedades modernas, ya que muchos servidores de correo no los soportan.
                            </v-alert>
                            <HtmlDropzone
                                v-model="htmlContent"
                                @getHtml="(htmlContent) => (form.contenido = htmlContent)"
                            />
                        </v-col>
                    </v-row>
                </v-card>

                <v-card class="pa-4 mb-4" elevation="1">
                    <v-card-title class="subtitle-1 font-weight-bold d-flex justify-space-between align-center">
                        <div>Lista de correos</div>
                        <div class="d-flex gap-2">
                            <v-menu offset-y>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn color="accent" elevation="0" v-bind="attrs" v-on="on" icon>
                                        <v-icon>mdi-help-circle-outline</v-icon>
                                    </v-btn>
                                </template>
                                <v-card style="max-width: 300px">
                                    <v-card-title class="subtitle-1 font-weight-bold">
                                        Instrucciones para el archivo Excel
                                    </v-card-title>
                                    <v-divider />
                                    <v-card-text>
                                        <ul style="padding-left: 1.2rem; margin: 0">
                                            <li>El archivo debe estar en formato .xlsx, .xls o .csv</li>
                                            <li>La primera columna debe incluir únicamente los correos electrónicos</li>
                                            <li>Los correos deben estar en formato válido (ejemplo@dominio.com)</li>
                                        </ul>
                                    </v-card-text>
                                </v-card>
                            </v-menu>

                            <v-btn color="primary" @click="openInputExcel">
                                <v-icon left>mdi-file-excel</v-icon>
                                <!-- <div v-if="!excelUploaded">Agregar archivo Excel</div>
                                <div v-else>Archivo agregado</div> -->
                                <div>Agregar archivo Excel</div>
                                <input
                                    ref="fileInputExcel"
                                    type="file"
                                    accept=".xlsx,.xls,.csv"
                                    style="display: none"
                                    @change="onAddExcel"
                                />
                            </v-btn>

                            o

                            <v-btn color="secondary" @click="onAddCorreo" outlined>
                                <v-icon left>mdi-email-plus</v-icon>
                                Agregar nuevo correo
                            </v-btn>
                        </div>
                    </v-card-title>
                    <v-divider class="mb-4" />

                    <v-simple-table dense class="correo-table">
                        <thead style="background: #f5f5f7">
                            <tr style="color: #444; font-weight: 600; font-size: 1rem">
                                <th class="text-left">#</th>
                                <th class="text-left">Correo</th>
                                <th class="text-left">Estado</th>
                                <th class="text-left">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="correos.length === 0">
                                <td colspan="4" class="text-center">No hay correos agregados.</td>
                            </tr>
                            <template v-else>
                                <tr v-for="(correo, idx) in correos" :key="correo.id">
                                    <td>{{ idx + 1 }}</td>
                                    <td>
                                        <v-text-field
                                            v-model="correo.email"
                                            type="email"
                                            required
                                            :rules="[
                                                (v) => !!v || 'El correo es requerido',
                                                (v) => validateEmail(v) || 'Correo inválido',
                                            ]"
                                            dense
                                            @input="
                                                correo.en_lista_negra = resources.lista_negra.includes(correo.email)
                                            "
                                        />
                                    </td>
                                    <td>
                                        <v-chip :color="correo.en_lista_negra ? 'error' : 'success'" dark small>
                                            <v-icon left small>
                                                {{ correo.en_lista_negra ? 'mdi-block-helper' : 'mdi-check-circle' }}
                                            </v-icon>
                                            {{ correo.en_lista_negra ? 'En lista negra' : 'Activo' }}
                                        </v-chip>
                                    </td>
                                    <td>
                                        <v-btn icon @click="onDeleteCorreo(idx)">
                                            <v-icon color="error">mdi-delete</v-icon>
                                        </v-btn>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </v-simple-table>
                </v-card>
            </v-col>

            <v-col cols="12" md="10" class="d-flex justify-center gap-3">
                <v-btn color="error" outlined @click="onResetForm" :disabled="submitFlag" :loading="submitFlag">
                    <v-icon left>mdi-refresh</v-icon>
                    Resetear formulario
                </v-btn>
                <v-btn color="primary" @click="onSubmit" :disabled="submitFlag" :loading="submitFlag">
                    <v-icon left>mdi-send</v-icon>
                    Enviar correos
                </v-btn>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
    import axios from '@/plugins/axios';

    const services = {
        getResources: async () => {
            return axios.get('/api/panel/envio-masivo/resources');
        },
        uploadExcel: async (file) => {
            const formData = new FormData();
            formData.append('file', file);
            return axios.post('/api/panel/envio-masivo/upload-excel', formData);
        },
        sendEmails: async (data) => {
            return axios.post('/api/panel/envio-masivo/enviar', data);
        },
    };

    import HtmlDropzone from '@/components/HtmlDropzone.vue';

    export default {
        components: { HtmlDropzone },
        data() {
            return {
                resources: {
                    lista_negra: [],
                    remitentes: [],
                },
                excelUploaded: false,
                htmlContent: null,
                form: {
                    asunto: '',
                    contenido: '',
                    remitente: '',
                    bcc: '',
                },
                correos: [],
                submitFlag: false,
            };
        },
        methods: {
            validateEmail: (email) => {
                const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                return re.test(String(email).toLowerCase());
            },

            openInputExcel() {
                const fileInput = this.$refs.fileInputExcel;
                if (fileInput) {
                    fileInput.click();
                }
            },
            async onAddExcel() {
                try {
                    this.excelUploaded = false;
                    const fileInput = this.$refs.fileInputExcel;
                    if (fileInput && fileInput.files.length) {
                        const file = fileInput.files[0];
                        const { data } = await services.uploadExcel(file);

                        this.$toasted.success(data.message || 'Archivo Excel agregado correctamente');
                        this.excelUploaded = true;

                        this.correos = data.correos || [];
                        // this.correos = [...this.correos, ...(data.correos || [])];
                    }
                } catch (error) {
                    console.error('Error al agregar archivo Excel:', error);
                    this.$toasted.error('Error al agregar archivo Excel');
                } finally {
                    this.excelUploaded = false;
                    this.$refs.fileInputExcel.value = null;
                }
            },

            onAddCorreo() {
                this.correos.push({
                    id: Date.now(),
                    email: '',
                    en_lista_negra: false,
                });
            },
            onDeleteCorreo(idx) {
                this.correos.splice(idx, 1);
            },

            clearForm() {
                this.correos = [];
                this.form = {
                    asunto: '',
                    contenido: '',
                    remitente: '',
                    bcc: '',
                };

                this.$refs.fileInputExcel.value = null;
                this.excelUploaded = false;
                this.htmlContent = null;
            },
            onResetForm() {
                this.clearForm();
                this.$toasted.success('Formulario reseteado correctamente');
            },
            async onSubmit() {
                if (this.submitFlag) {
                    return
                }

                this.submitFlag = true;

                try {
                    if (!this.form.asunto || !this.form.remitente || !this.form.contenido) {
                        this.$toasted.error('Por favor, completa todos los campos requeridos');
                        return;
                    }
                    if (this.correos.length === 0) {
                        this.$toasted.error('Por favor, agrega al menos un correo');
                        return;
                    }

                    const { data } = await services.sendEmails({
                        ...this.form,
                        correos: this.correos,
                    });

                    this.clearForm();

                    this.$toasted.success(data.message || 'Correos enviados correctamente');
                } catch (error) {
                    console.error('Error al enviar los correos:', error);
                    this.$toasted.error(error.response?.data?.message || 'Error al enviar los correos');
                } finally {
                    this.submitFlag = false;
                }
            },
        },
        async mounted() {
            const { data } = await services.getResources();
            this.resources = data;
        },
    };
</script>

<style scoped>
    .gap-1 {
        gap: 8px;
    }
    .gap-2 {
        gap: 16px;
    }
    .gap-3 {
        gap: 24px;
    }

    .dropzone-html {
        transition: border-color 0.2s;
    }
</style>
